import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCourse } from 'hooks/useCourse';
import { assessmentApi } from 'configs/api';
import { courseApi } from 'configs/api';
import Button from 'themeComponents/Button';
import Box from 'themeComponents/Box';
import Typography from 'themeComponents/Typography';
import DashboardLayout from 'themeComponents/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'themeComponents/Navbars/DashboardNavbar';
import Footer from 'themeComponents/Footer';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { Visibility, Edit, Add, ArrowBack, Delete, CheckCircle } from '@mui/icons-material';

export default function AssessmentsList() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { getCourse } = useCourse();
  const [assessments, setAssessments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [assessmentToDelete, setAssessmentToDelete] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);

  const normalizeAssessments = (payload: any): any[] => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.data?.data)) return payload.data.data;
    return [];
  };

  const normalizeCourse = (payload: any): any => {
    if (payload?.data && typeof payload.data === 'object' && !Array.isArray(payload.data)) {
      return payload.data;
    }
    return payload || {};
  };

  const handleDeleteClick = (assessment: any) => {
    setAssessmentToDelete(assessment);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!assessmentToDelete?.id) return;

    setDeleting(true);
    try {
      await assessmentApi.delete(`/api/assessments/${assessmentToDelete.id}`);
      setAssessments(assessments.filter(a => a.id !== assessmentToDelete.id));
      setDeleteDialogOpen(false);
      setAssessmentToDelete(null);
    } catch (e: any) {
      console.error('Failed to delete assessment:', e);
      setError(e?.response?.data?.message || e?.message || 'Failed to delete assessment');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setAssessmentToDelete(null);
  };

  const handleFinishCourseSetup = () => {
    navigate(`/courses/${courseId}`, { replace: true });
  };

  useEffect(() => {
    let isMounted = true;

    async function fetchData() {
      if (!courseId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');

      try {
        // Fetch assessments for this course (primary data for this page)
        const res = await assessmentApi.get(`/api/assessments/course/${courseId}`);
        let list = normalizeAssessments(res?.data ?? res);

        // Fallback: some environments expose this endpoint on courseApi base URL
        if (list.length === 0) {
          const fallbackRes = await courseApi.get(`/api/assessments/course/${courseId}`);
          const fallbackList = normalizeAssessments(fallbackRes?.data ?? fallbackRes);
          list = fallbackList;
        }

        if (isMounted) {
          // Keep a simple fallback for cases where backend returns plain array
          setAssessments(Array.isArray(list) ? list : []);
        }

        // Course fetch should not block assessments rendering
        try {
          const courseData = await getCourse(courseId);
          const normalizedCourse = normalizeCourse(courseData);
          if (normalizedCourse?.id && normalizedCourse.id !== courseId) {
            console.warn('Course id mismatch between route and course payload');
          }
        } catch (courseError) {
          console.warn('[AssessmentsList] getCourse failed but assessments fetch succeeded', courseError);
        }

      } catch (e: any) {
        if (isMounted) {
          setAssessments([]);
          setError(e?.response?.data?.message || e?.message || 'Failed to load assessments');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [courseId, getCourse]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box py={3}>
        <Box mb={3} display="flex" justifyContent="space-between" alignItems={{ xs: 'start', md: 'center' }} flexDirection={{ xs: 'column', md: 'row' }} gap={2}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <IconButton size="small" onClick={() => navigate(`/courses/${courseId}/modules`)}>
              <ArrowBack fontSize="small" />
            </IconButton>
            <Box>
              <Typography variant="h4" fontWeight="bold">Assessments</Typography>
              <Typography variant="body2" color="text">Step 3 of 3 - Create and manage course assessments</Typography>
            </Box>
          </Box>
          <Box display="flex" gap={1} flexDirection={{ xs: 'column', sm: 'row' }}>
            <Button variant="gradient" color="info" onClick={() => navigate(`/courses/${courseId}/assessments/add`)}>
              <Add sx={{ mr: 0.5 }} /> Add Assessment
            </Button>
            <Button
              variant="gradient"
              color="success"
              onClick={handleFinishCourseSetup}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <CheckCircle fontSize="small" /> Finish Setup
            </Button>
          </Box>
        </Box>
        <Box mt={3} sx={{ position: 'relative', zIndex: 2 }}>
          {loading ? (
            <Typography>Loading...</Typography>
          ) : error ? (
            <Typography color="error">{error}</Typography>
          ) : assessments.length === 0 ? (
            <Box
              sx={{
                border: '1px dashed',
                borderColor: 'info.main',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                backgroundColor: '#ffffff',
                boxShadow: 3,
              }}
            >
              <Typography variant="h6" fontWeight="bold" color="dark" mb={1}>
                No assessments added yet
              </Typography>
              <Typography variant="body2" color="text" mb={2}>
                Create your first assessment to define pass criteria and unlock module progression.
              </Typography>
              <Button
                variant="gradient"
                color="info"
                onClick={() => navigate(`/courses/${courseId}/assessments/add`)}
              >
                <Add sx={{ mr: 0.5 }} /> Add First Assessment
              </Button>
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" gap={2}>
              {assessments.map((a, i) => (
                <Card key={a.id || i} sx={{ backgroundColor: '#fff', border: '1px solid', borderColor: 'divider', transition: 'all 0.2s ease', '&:hover': { boxShadow: 6, borderColor: 'info.main' } }}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="start" gap={2}>
                      <Box flex={1}>
                        <Typography variant="h6" fontWeight="bold" color="dark" mb={0.5}>
                          {a.title || 'Untitled Assessment'}
                        </Typography>
                        {a.description ? (
                          <Typography variant="body2" color="text">
                            {a.description}
                          </Typography>
                        ) : null}
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Tooltip title="View assessment">
                          <span>
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => navigate(`/courses/${courseId}/assessments/${a.id}`, { state: { mode: 'view' } })}
                              disabled={!a.id}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Edit assessment">
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => navigate(`/courses/${courseId}/assessments/${a.id}`)}
                              disabled={!a.id}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete assessment">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(a)}
                              disabled={!a.id}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </Box>
                    <Box mt={1.5} display="flex" gap={1} flexWrap="wrap">
                      <Chip label={`Passing Score: ${a.passingScore ?? 0}%`} size="small" color="info" variant="outlined" />
                      <Chip label={`Questions: ${Array.isArray(a.questions) ? a.questions.length : 0}`} size="small" color="secondary" variant="outlined" />
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Assessment</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete &quot;{assessmentToDelete?.title || 'Untitled Assessment'}&quot;? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} variant="gradient" color="error" disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}
